import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';

interface Coordinate {
  lat: number;
  lng: number;
}

interface Tip {
  id: number;
  commentText: string;
  latitude: number;
  longitude: number;
  tipType: string; // Caution, SpeedLimit, LaneSelection, General
  createdAt: string;
  instructorName: string;
}

interface TestRoutesMapProps {
  coordinates: Coordinate[];
  tips: Tip[];
  onAddTip?: (tip: { commentText: string; latitude: number; longitude: number; tipType: string }) => Promise<void>;
  isInstructor?: boolean;
  isAdminRecording?: boolean;
  onRecordCoordinate?: (coord: Coordinate) => void;
  onClearRecorded?: () => void;
}

const TestRoutesMap: React.FC<TestRoutesMapProps> = ({
  coordinates,
  tips,
  onAddTip,
  isInstructor = false,
  isAdminRecording = false,
  onRecordCoordinate,
  onClearRecorded,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredTip, setHoveredTip] = useState<Tip | null>(null);
  const [hoveredTipPos, setHoveredTipPos] = useState({ x: 0, y: 0 });

  // Dialog for adding a tip
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clickedCoord, setClickedCoord] = useState<Coordinate | null>(null);
  const [commentText, setCommentText] = useState('');
  const [tipType, setTipType] = useState('Caution');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Map Bounds for auto-scaling
  const [bounds, setBounds] = useState({
    minLat: 51.5,
    maxLat: 51.6,
    minLng: -0.15,
    maxLng: -0.05,
  });

  useEffect(() => {
    if (coordinates.length > 0) {
      const lats = coordinates.map((c) => c.lat);
      const lngs = coordinates.map((c) => c.lng);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Add a small padding buffer
      const latPadding = Math.max((maxLat - minLat) * 0.15, 0.005);
      const lngPadding = Math.max((maxLng - minLng) * 0.15, 0.005);

      setBounds({
        minLat: minLat - latPadding,
        maxLat: maxLat + latPadding,
        minLng: minLng - lngPadding,
        maxLng: maxLng + lngPadding,
      });
      // Reset Pan
      setPan({ x: 0, y: 0 });
      setZoom(1);
    } else {
      // Default to London coordinates if empty
      setBounds({
        minLat: 51.58,
        maxLat: 51.62,
        minLng: -0.13,
        maxLng: -0.07,
      });
    }
  }, [coordinates]);

  // Coordinate Projection Helper (Lat/Lng to local SVG percentage)
  const project = (lat: number, lng: number) => {
    const latDiff = bounds.maxLat - bounds.minLat;
    const lngDiff = bounds.maxLng - bounds.minLng;

    // SVG coordinate space
    const width = 800;
    const height = 500;

    // Calculate relative percentage
    const xPct = (lng - bounds.minLng) / (lngDiff || 1);
    // Latitudes decrease as you go south, so invert Y
    const yPct = 1 - (lat - bounds.minLat) / (latDiff || 1);

    return {
      x: xPct * width,
      y: yPct * height,
    };
  };

  // Convert SVG local point back to Lat/Lng (for click-to-add / record)
  const unproject = (x: number, y: number) => {
    const latDiff = bounds.maxLat - bounds.minLat;
    const lngDiff = bounds.maxLng - bounds.minLng;

    const width = 800;
    const height = 500;

    const xPct = x / width;
    const yPct = 1 - y / height;

    return {
      lat: bounds.minLat + yPct * latDiff,
      lng: bounds.minLng + xPct * lngDiff,
    };
  };

  // Map Navigation Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Left click only
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = 1.1;
    let newZoom = zoom;
    if (e.deltaY < 0) {
      newZoom = Math.min(zoom * zoomFactor, 8);
    } else {
      newZoom = Math.max(zoom / zoomFactor, 0.5);
    }
    setZoom(newZoom);
  };

  // Click on map to add a point or record coordinates
  const handleMapClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging) return;

    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();

    // Get click position relative to the local viewport
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Apply zoom and pan inverse transformations to get raw SVG coordinate
    const svgX = (clickX - rect.width / 2 - pan.x) / zoom + 400;
    const svgY = (clickY - rect.height / 2 - pan.y) / zoom + 250;

    const coord = unproject(svgX, svgY);

    if (isAdminRecording && onRecordCoordinate) {
      onRecordCoordinate(coord);
    } else if (isInstructor && onAddTip) {
      setClickedCoord(coord);
      setDialogOpen(true);
    }
  };

  const handleSaveTip = async () => {
    if (!commentText.trim() || !clickedCoord || !onAddTip) return;
    try {
      setIsSubmitting(true);
      await onAddTip({
        commentText: commentText.trim(),
        latitude: clickedCoord.lat,
        longitude: clickedCoord.lng,
        tipType,
      });
      setCommentText('');
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get icon based on tip type
  const renderTipIcon = (type: string) => {
    switch (type) {
      case 'Caution':
        return <WarningAmberIcon style={{ color: '#ff1744', fontSize: 18 }} />;
      case 'SpeedLimit':
        return <SpeedIcon style={{ color: '#ff9100', fontSize: 18 }} />;
      case 'LaneSelection':
        return <AltRouteIcon style={{ color: '#2979ff', fontSize: 18 }} />;
      default:
        return <ChatBubbleOutlineIcon style={{ color: '#00e676', fontSize: 18 }} />;
    }
  };

  // Path polyline points string builder
  const buildPathString = () => {
    return coordinates.map((c) => {
      const p = project(c.lat, c.lng);
      return `${p.x},${p.y}`;
    }).join(' ');
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: '#0b0f19', border: '1px solid #1e293b' }}>
      {/* Control Overlay */}
      <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', gap: 1, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
            startIcon={<MyLocationIcon />}
            sx={{
              textTransform: 'none',
              bgcolor: 'rgba(30, 41, 59, 0.85)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(30, 41, 59, 0.95)' }
            }}
          >
            Recenter
          </Button>
          {isAdminRecording && (
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={onClearRecorded}
              startIcon={<DeleteOutlineIcon />}
              sx={{ textTransform: 'none' }}
            >
              Clear Points
            </Button>
          )}
        </Box>
        {isInstructor && !isAdminRecording && (
          <Alert severity="info" sx={{ py: 0.2, px: 1, '& .MuiAlert-icon': { fontSize: 18 } }}>
            Instructors: Click anywhere along the route path to pin a tip.
          </Alert>
        )}
        {isAdminRecording && (
          <Alert severity="warning" sx={{ py: 0.2, px: 1, '& .MuiAlert-icon': { fontSize: 18 } }}>
            Recording Mode: Click the grid sequentially to draw the route path.
          </Alert>
        )}
      </Box>

      {/* SVG Map Canvas */}
      <Box
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        sx={{
          cursor: isDragging ? 'grabbing' : isAdminRecording || isInstructor ? 'crosshair' : 'grab',
          height: 500,
          width: '100%',
          position: 'relative',
        }}
      >
        <svg
          viewBox="0 0 800 500"
          width="100%"
          height="100%"
          onClick={handleMapClick}
          style={{ display: 'block', overflow: 'hidden' }}
        >
          {/* Blueprint Grid Pattern */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            </pattern>
            <pattern id="grid-fine" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.015)" strokeWidth="0.5" />
            </pattern>
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <filter id="pulse" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
            </filter>
          </defs>

          {/* Background Grids */}
          <rect width="100%" height="100%" fill="#090d16" />
          <rect width="100%" height="100%" fill="url(#grid-fine)" />
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Zoomed/Paged layer */}
          <g transform={`translate(${400 + pan.x}, ${250 + pan.y}) scale(${zoom}) translate(-400, -250)`}>
            {/* Start Location Node */}
            {coordinates.length > 0 && (
              <g>
                <circle
                  cx={project(coordinates[0].lat, coordinates[0].lng).x}
                  cy={project(coordinates[0].lat, coordinates[0].lng).y}
                  r="12"
                  fill="rgba(33, 150, 243, 0.2)"
                />
                <circle
                  cx={project(coordinates[0].lat, coordinates[0].lng).x}
                  cy={project(coordinates[0].lat, coordinates[0].lng).y}
                  r="6"
                  fill="#2196f3"
                  filter="url(#glow)"
                />
                <text
                  x={project(coordinates[0].lat, coordinates[0].lng).x + 12}
                  y={project(coordinates[0].lat, coordinates[0].lng).y + 4}
                  fill="#ffffff"
                  fontSize="10"
                  fontWeight="bold"
                  style={{ userSelect: 'none' }}
                >
                  START
                </text>
              </g>
            )}

            {/* Route Polyline Track */}
            {coordinates.length > 1 && (
              <polyline
                points={buildPathString()}
                fill="none"
                stroke="#1e3a8a"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {coordinates.length > 1 && (
              <polyline
                points={buildPathString()}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
              />
            )}

            {/* Path Coordinates Node Markers (Admin mode) */}
            {isAdminRecording &&
              coordinates.map((c, idx) => {
                const p = project(c.lat, c.lng);
                return (
                  <circle
                    key={idx}
                    cx={p.x}
                    cy={p.y}
                    r="4"
                    fill="#3b82f6"
                    stroke="#ffffff"
                    strokeWidth="1"
                  />
                );
              })}

            {/* Instructor Tip Map Markers */}
            {tips.map((tip) => {
              const p = project(tip.latitude, tip.longitude);
              let markerBg = '#00e676';
              if (tip.tipType === 'Caution') markerBg = '#ff1744';
              else if (tip.tipType === 'SpeedLimit') markerBg = '#ff9100';
              else if (tip.tipType === 'LaneSelection') markerBg = '#2979ff';

              const isHovered = hoveredTip?.id === tip.id;

              return (
                <g
                  key={tip.id}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      setHoveredTip(tip);
                      // Calculate tip screen coordinate for absolute positioning of popover
                      const zoomX = (p.x - 400) * zoom + 400 + pan.x;
                      const zoomY = (p.y - 250) * zoom + 250 + pan.y;
                      setHoveredTipPos({ x: zoomX, y: zoomY });
                    }
                  }}
                  onMouseLeave={() => setHoveredTip(null)}
                >
                  {/* Radar Pulse for Caution */}
                  {tip.tipType === 'Caution' && (
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isHovered ? "20" : "15"}
                      fill="none"
                      stroke="#ff1744"
                      strokeWidth="2"
                      opacity={isHovered ? "0.6" : "0.3"}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <animate
                        attributeName="r"
                        values="8;20;8"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.8;0.2;0.8"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  )}

                  {/* Outer glow ring */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? "12" : "9"}
                    fill={markerBg}
                    opacity="0.25"
                    style={{ transition: 'all 0.2s' }}
                  />

                  {/* Marker Pin */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? "7" : "5"}
                    fill={markerBg}
                    stroke="#ffffff"
                    strokeWidth="1.5"
                    filter="url(#glow)"
                    style={{ transition: 'all 0.2s' }}
                  />
                </g>
              );
            })}
          </g>
        </svg>

        {/* Hover Tooltip Popover (Standard CSS overlay instead of standard svg title for rich visuals) */}
        {hoveredTip && (
          <Box
            sx={{
              position: 'absolute',
              left: hoveredTipPos.x,
              top: hoveredTipPos.y - 12,
              transform: 'translate(-50%, -100%)',
              bgcolor: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${
                hoveredTip.tipType === 'Caution'
                  ? 'rgba(255, 23, 68, 0.3)'
                  : hoveredTip.tipType === 'SpeedLimit'
                  ? 'rgba(255, 145, 0, 0.3)'
                  : 'rgba(255,255,255,0.08)'
              }`,
              borderRadius: 3,
              p: 2,
              width: 250,
              boxShadow: '0 12px 30px rgba(0,0,0,0.5)',
              pointerEvents: 'none',
              zIndex: 100,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {renderTipIcon(hoveredTip.tipType)}
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'white', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                {hoveredTip.tipType} Alert
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1.5, lineHeight: 1.4 }}>
              "{hoveredTip.commentText}"
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                By {hoveredTip.instructorName}
              </Typography>
              <Chip
                label={new Date(hoveredTip.createdAt).toLocaleDateString()}
                size="small"
                sx={{ height: 16, fontSize: 9, bgcolor: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Dialog to add a Tip */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 'bold' }}>Pin Instructor Tip</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Pin a warning or guide hotspot at coordinates ({clickedCoord?.lat.toFixed(5)}, {clickedCoord?.lng.toFixed(5)}) along the route path.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Tip Category</InputLabel>
              <Select value={tipType} label="Tip Category" onChange={(e) => setTipType(e.target.value)}>
                <MenuItem value="Caution">⚠️ Caution / Hazard Hotspot</MenuItem>
                <MenuItem value="SpeedLimit">🚗 Speed Limit Alert</MenuItem>
                <MenuItem value="LaneSelection">↪️ Lane / Roundabout Guidance</MenuItem>
                <MenuItem value="General">💬 General Tip</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Tip Description"
              multiline
              rows={3}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="e.g. Watch out for lane changes here, students often get sorted into the wrong exit lane."
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTip}
            variant="contained"
            disabled={!commentText.trim() || isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Pin Marker
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestRoutesMap;
